import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	KnowledgeDiscoverySearchChatIntentDetectionModelVersion,
	type KnowledgeDiscoverySearchChatIntentDetectionModelVersionType,
} from '@atlassian/search-client/knowledge-discovery/types';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { PrimaryProductKeys } from '../../constants/primary-product-keys';

export type ChatSurface = 'new-tab' | 'sidebar' | 'floaty' | 'floaty-big';

const CHAT_CONVERGENCE_EXPERIMENT = 'rovo_search_chat_convergence_experiment';

/**
 * Resolves which chat convergence surface should be served.
 *
 * The experiment cohorts map to the implemented surfaces:
 *
 *   sidebar        -> sidebar
 *   floaty-normal  -> floaty
 *   floaty-big     -> floaty-big
 *   new-tab        -> new tab
 *   intent-model-b -> floaty with the experimental intent-detection model
 *
 * All testing gates are evaluated before the experiment. If any gate is enabled, its surface is
 * served without evaluating the experiment and recording its exposure. When multiple gates are
 * enabled, the order is: new tab > sidebar > floaty big > floaty. Gates and every cohort other
 * than intent-model-b use the baseline intent-detection model.
 */
export const serveChatConvergenceExperiment = (
	primaryProduct: string | undefined,
	{ fireExposure = false }: { fireExposure?: boolean } = {},
): {
	chatIntentDetectionEnabled: boolean;
	chatSurface: ChatSurface | undefined;
	intentDetectionModel: KnowledgeDiscoverySearchChatIntentDetectionModelVersionType | undefined;
} => {
	const isSearchChatConvergenceProduct = primaryProduct === PrimaryProductKeys.Confluence;

	if (!isSearchChatConvergenceProduct) {
		return {
			chatIntentDetectionEnabled: false,
			chatSurface: undefined,
			intentDetectionModel: undefined,
		};
	}

	const sidebarChatEnabled = fg('rovo_search_chat_convergence_sidebar');
	const floatyChatEnabled = fg('rovo_search_chat_convergence_floaty');
	const floatyBigChatEnabled = fg('rovo_search_chat_convergence_floaty_big');
	const newTabChatEnabled = fg('rovo_search_chat_convergence_tab');

	if (newTabChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'new-tab',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	if (sidebarChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'sidebar',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	if (floatyBigChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'floaty-big',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	if (floatyChatEnabled) {
		return {
			chatIntentDetectionEnabled: true,
			chatSurface: 'floaty',
			intentDetectionModel:
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1,
		};
	}

	const { cohort } = getFrontendExperiment({
		abTestId: CHAT_CONVERGENCE_EXPERIMENT,
		fireExperimentExposure: fireExposure,
	});
	let chatSurface: ChatSurface | undefined;
	let intentDetectionModel: KnowledgeDiscoverySearchChatIntentDetectionModelVersionType | undefined;
	switch (cohort) {
		case 'new-tab':
			chatSurface = 'new-tab';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'sidebar':
			chatSurface = 'sidebar';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'floaty-normal':
			chatSurface = 'floaty';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'floaty-big':
			chatSurface = 'floaty-big';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA3_1B_SCI_V1;
			break;
		case 'intent-model-b':
			chatSurface = 'floaty';
			intentDetectionModel =
				KnowledgeDiscoverySearchChatIntentDetectionModelVersion.GEMMA4_E2B_SCI_V1;
			break;
	}

	return {
		chatIntentDetectionEnabled: chatSurface !== undefined,
		chatSurface,
		intentDetectionModel,
	};
};
